import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';

/**
 * Hook: useDownloadPascoStream
 * Downloads a PASCO file as a blob stream and triggers a browser download
 * 
 * @param options - React Query mutation options
 * @returns Mutation result with download function
 * 
 * @example
 * ```typescript
 * const { mutate: downloadPasco, isPending } = useDownloadPascoStream({
 *     onSuccess: () => {
 *         ToastService.success('Download started successfully');
 *     },
 *     onError: (error) => {
 *         ToastService.error('Failed to download file');
 *     },
 * });
 * 
 * // Trigger download
 * downloadPasco({ pascoId: '123', filename: 'pasco.pdf' });
 * ```
 */
export function useDownloadPascoStream(
    options?: Omit<
        UseMutationOptions<void, Error, { pascoId: string; filename?: string }>,
        'mutationFn'
    >
) {
    const pascoService = getPascoService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async ({ pascoId, filename }: { pascoId: string; filename?: string }) => {
            if (!pascoId) {
                throw new Error('Pasco ID is required');
            }

            // Download the blob from the API
            const blob = await pascoService.downloadStream(pascoId);

            // Create a blob URL and trigger download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || `pasco-${pascoId}.pdf`;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Refresh credit balance after successful download (consumes credits)
            if (import.meta.env.DEV) {
                console.log('🔄 Download completed, refetching credit balance...');
            }

            // Force refetch the credit balance
            await queryClient.refetchQueries({
                queryKey: ['credits', 'balance'],
                type: 'active'
            });

            if (import.meta.env.DEV) {
                console.log('✅ Credit balance refetch completed');
            }

            // Call user's onSuccess if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        },
    });
}
